import React, { FC, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import styles from '../../../assets/provider-tab.module.scss';
import Form from '../components/editSession/SeanceForm';
import { ISeance } from 'src/types/seance';

import { IProvider } from 'src/types/provider';
import Loader from 'src/components/Loader';
import { changeToZeroZoneDate } from 'src/helpers/timeHelpers';
import { QueryParams } from 'src/types/grid';
import { Paginated } from 'src/services/api-handlers/pagination';
import { IServiceType } from 'src/types/service-types';
import { IServiceLocation } from 'src/types/service-locations';
import user from 'src/services/user-auth';
import UserRole from 'src/enumerables/user-role';
import { ProgramBookApi } from '../../../../../../../../../services/end-points';

export interface StateProps {
    seance: ISeance | null;
    valuesForm: Partial<ISeance>;
    pgValuesForm: any;
    programBooks: any;
    programBookAnswers: any;
    inProgressAnswers: boolean;
    inProgress: boolean;
    serviceTypes: Paginated<IServiceType>;
    seances: Paginated<ISeance>;
    serviceLocations: Paginated<IServiceLocation>;
    files: any;
}
export interface DispatchProps {
    getData: (id: string | number) => void;
    getFiles: (id: string | number) => void;
    getProgramBooks: (param: QueryParams) => void;
    getProgramBookAnswers: (param: QueryParams) => void;
    addFiles: (id: string | number, requestType: string, files: any, isDatasheet: boolean) => void;
    removeFile: (id: string | number) => void;
    getServiceTypes: (param: QueryParams) => void;
    getSessionsData: (param: QueryParams) => void;
    getServiceLocations: (param: QueryParams) => void;
}

export interface OwnProps {
    provider: Partial<IProvider> | null;
    seanceId: string;
    tab?: string;
}

export interface Props extends StateProps, DispatchProps, OwnProps {}
const SeanceEditScreen: FC<Props> = (props: Props) => {
    const [isLoader, setLoader] = useState<boolean>(true);
    const [useLoaderInProgress, setUseLoaderInProgress] = useState<boolean>(true);

    const [notesAnswersLoading, setNotesAnswersLoading] = useState(false);
    const [notesProgramBookAnswers, setNotesProgramBookAnswers] = useState<any>(null);
    const lastKeyRef = React.useRef<string | null>(null);

    const {
        seance,
        programBooks,
        programBookAnswers,
        valuesForm,
        pgValuesForm,
        seanceId,
        getFiles,
        getProgramBooks,
        getProgramBookAnswers,
        files,
        removeFile,
        getData,
        inProgress,
        inProgressAnswers,
        seances,
        serviceLocations,
        serviceTypes,
        addFiles,
        getServiceTypes,
        getSessionsData,
        getServiceLocations,
    } = props;

    useEffect(() => {
        if (seanceId) {
            getData(seanceId);
            getFiles(seanceId);
            getServiceTypes({
                page: 1,
                itemsPerPage: 1200,
            });
            getServiceLocations({
                page: 1,
                itemsPerPage: 1200,
            });
        }
        // eslint-disable-next-line
    }, [getData]);

    useEffect(() => {
        if (seance?.student?.id) {
            getProgramBooks({
                filters: {
                    //'orderEntity.id': seance?.occasion?.orderEntity?.id,
                    'student.id': seance?.student?.id,
                },
            });
        }
    }, [seance, getData, getSessionsData]);

    /*useEffect(() => {
        if (!seance?.id || !programBooks.data.list?.length) return;
        const programBook = programBooks.data.list[0];
        getProgramBookAnswers({
            filters: {
                'programBook.id': programBook ? programBook.id : null,
                'seance.id': seance?.id,
            },
        });
    }, [programBooks, seance?.id]);*/

    useEffect(() => {
        if (!programBooks.data.list?.length) return;
        const programBook = programBooks.data.list[0];
        const programBookId = programBook ? programBook.id : null;
        if (!seance?.id || !programBookId) return;

        const key = `${programBookId}:${seance.id}`;
        if (lastKeyRef.current === key) return;
        lastKeyRef.current = key;

        let cancelled = false;

        (async () => {
            try {
                setNotesAnswersLoading(true);

                const resp = await ProgramBookApi.getProgramBookAnswersForNotes({
                    programBook: programBookId,
                    seance: seance.id,
                    'axios-retry': { retries: 3 },
                });
                const list = resp.data?.data ?? [];

                if (cancelled) return;

                setNotesProgramBookAnswers({
                    data: { list, totalCount: list.length },
                });
            } catch (e) {
                console.error(e);
            } finally {
                if (!cancelled) setNotesAnswersLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [programBooks, seance?.id]);

    // After any programBook answer save completes, re-fetch notes so initialValues
    // is updated with the freshly saved data from the server.
    const prevInProgressAnswersRef = useRef(false);
    useEffect(() => {
        const was = prevInProgressAnswersRef.current;
        const is = !!inProgressAnswers;
        prevInProgressAnswersRef.current = is;

        if (!was || is) return; // only fire on true → false transition

        if (!programBooks.data.list?.length || !seance?.id) return;
        const programBook = programBooks.data.list[0];
        const programBookId = programBook?.id;
        if (!programBookId) return;

        let cancelled = false;
        (async () => {
            try {
                setNotesAnswersLoading(true);
                const resp = await ProgramBookApi.getProgramBookAnswersForNotes({
                    programBook: programBookId,
                    seance: seance.id,
                    'axios-retry': { retries: 3 },
                });
                const list = resp.data?.data ?? [];
                if (!cancelled) {
                    setNotesProgramBookAnswers({ data: { list, totalCount: list.length } });
                }
            } catch (e) {
                console.error(e);
            } finally {
                if (!cancelled) setNotesAnswersLoading(false);
            }
        })();

        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inProgressAnswers]);

    const getSessions = () => {
        getSessionsData({
            page: 1,
            itemsPerPage: 10,
            sorting: { seanceDate: 'desc' },
            filters: { 'occasion.id': seance?.occasion?.id, current: seance?.id, currentDate: seance?.startTime },
        });
    };
    const handeAdd = (files: any, requestType: string, isDatasheet: boolean) => {
        addFiles(seanceId, requestType, files, isDatasheet);
    };
    const handeRemove = (fileId: string | number) => {
        removeFile(fileId);
    };
    const dataSheetFiles: any = [];
    let otherFiles: any = [];
    if (seance?.occasion?.isDatasheetRequired) {
        files?.forEach((itF: any) => {
            if (itF.isDatasheet) {
                dataSheetFiles.push(itF);
            } else {
                otherFiles.push(itF);
            }
        });
    } else {
        otherFiles = files;
    }
    const dataSheet: string | undefined = seance?.datasheet && seance?.datasheet.join(',');
    const isAdmin = user.getRoles().includes(UserRole.ROLE_ADMIN);

    const programBookAnswersForForm = notesProgramBookAnswers ?? programBookAnswers;

    return (
        <div className={classNames(styles.tabWrap)}>
            {isLoader && (!seance || inProgress || notesAnswersLoading) ? <Loader /> : null}
            <Form
                files={otherFiles}
                programBookAnswers={programBookAnswersForForm}
                inProgressAnswers={inProgressAnswers || notesAnswersLoading}
                dataSheetFiles={dataSheetFiles}
                idProvider={`${props.provider?.id}`}
                valuesForm={valuesForm}
                pgValuesForm={pgValuesForm}
                seances={seances}
                isDatasheetRequired={seance?.occasion?.isDatasheetRequired}
                isDataSheetWait={!isAdmin && dataSheet === 'Waiting' && seance?.status === 4}
                signDoc={seance?.signDoc}
                setSignDoc={(files) => handeAdd([files], 'SIGNDOC', false)}
                setFiles={(files: any, isDatasheet: boolean) => handeAdd(files, 'OTHER', isDatasheet)}
                removeFile={(id) => handeRemove(id)}
                caseType={seance?.occasion?.caseType}
                setLoader={setLoader}
                serviceTypes={serviceTypes}
                getSessionsData={getSessions}
                serviceLocations={serviceLocations}
                initialValues={{
                    ...seance,
                    serviceLocation: seance?.serviceLocation?.id
                        ? { value: seance.serviceLocation?.id, label: seance.serviceLocation?.title }
                        : null,
                    serviceType: seance?.serviceType
                        ? { value: seance.serviceType?.id, label: seance.serviceType?.title }
                        : null,
                    occasion: seance?.occasion?.id,
                    seanceDate: seance?.seanceDate
                        ? changeToZeroZoneDate(seance['seanceDate'].replace(/ /g, 'T'))
                        : undefined,
                    startTime: seance?.startTime
                        ? changeToZeroZoneDate(seance.startTime.replace(/ /g, 'T'))
                        : undefined,
                    endTime: seance?.endTime ? changeToZeroZoneDate(seance.endTime.replace(/ /g, 'T')) : undefined,
                    startTimeOld: seance?.startTime
                        ? changeToZeroZoneDate(seance.startTime.replace(/ /g, 'T'))
                        : undefined,
                    endTimeOld: seance?.endTime ? changeToZeroZoneDate(seance.endTime.replace(/ /g, 'T')) : undefined,
                }}
            />
        </div>
    );
};

export default SeanceEditScreen;
